import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Monitor, CreditCard, BarChart3, ShoppingCart, ArrowRight, CheckCircle2, Layers, Zap, Globe, Smartphone } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>
  );
}

const POS_FEATURES_DATA = [
  { icon: Monitor, titleKey: "pos.featInterface", descKey: "pos.featInterfaceDesc" },
  { icon: CreditCard, titleKey: "pos.featPayment", descKey: "pos.featPaymentDesc" },
  { icon: BarChart3, titleKey: "pos.featAnalytics", descKey: "pos.featAnalyticsDesc" },
  { icon: ShoppingCart, titleKey: "pos.featStock", descKey: "pos.featStockDesc" },
  { icon: Layers, titleKey: "pos.featMulti", descKey: "pos.featMultiDesc" },
  { icon: Globe, titleKey: "pos.featOnline", descKey: "pos.featOnlineDesc" },
];

const POS_FEATURES_I18N = {
  fr: [
    { title: "Interface intuitive", desc: "Un systeme de caisse pense pour la rapidite et la simplicite d'utilisation quotidienne." },
    { title: "Paiement integre", desc: "Terminaux AyaPay directement connectes a votre caisse. Un seul systeme pour tout." },
    { title: "Analytics temps reel", desc: "Suivez vos ventes, stocks et performances depuis n'importe quel appareil." },
    { title: "Gestion des stocks", desc: "Suivi automatique des stocks, alertes de reapprovisionnement et rapports detailles." },
    { title: "Multi-sites", desc: "Gerez plusieurs points de vente depuis un tableau de bord centralise." },
    { title: "Commandes en ligne", desc: "QR codes, tablettes et commandes en ligne integrees a votre systeme." },
  ],
  de: [
    { title: "Intuitive Oberflache", desc: "Ein Kassensystem fur Schnelligkeit und einfache tagliche Nutzung." },
    { title: "Integrierte Zahlung", desc: "AyaPay-Terminals direkt mit Ihrer Kasse verbunden. Ein System fur alles." },
    { title: "Echtzeit-Analytics", desc: "Verfolgen Sie Verkaufe, Bestande und Leistung von jedem Gerat." },
    { title: "Bestandsverwaltung", desc: "Automatische Bestandsverfolgung, Nachbestellbenachrichtigungen." },
    { title: "Multi-Standorte", desc: "Verwalten Sie mehrere Standorte von einem Dashboard." },
    { title: "Online-Bestellungen", desc: "QR-Codes, Tablets und Online-Bestellungen integriert." },
  ],
  en: [
    { title: "Intuitive interface", desc: "A cash register system designed for speed and simplicity." },
    { title: "Integrated payment", desc: "AyaPay terminals directly connected to your register. One system for everything." },
    { title: "Real-time analytics", desc: "Track sales, inventory and performance from any device." },
    { title: "Stock management", desc: "Automatic stock tracking, replenishment alerts and detailed reports." },
    { title: "Multi-site", desc: "Manage multiple locations from a centralized dashboard." },
    { title: "Online orders", desc: "QR codes, tablets and online orders integrated into your system." },
  ],
  it: [
    { title: "Interfaccia intuitiva", desc: "Un sistema di cassa pensato per la velocita e la semplicita d'uso." },
    { title: "Pagamento integrato", desc: "Terminali AyaPay collegati alla cassa. Un unico sistema per tutto." },
    { title: "Analytics in tempo reale", desc: "Monitorate vendite, stock e performance da qualsiasi dispositivo." },
    { title: "Gestione stock", desc: "Tracciamento automatico, avvisi e report dettagliati." },
    { title: "Multi-sede", desc: "Gestite piu punti vendita da un pannello centralizzato." },
    { title: "Ordini online", desc: "QR code, tablet e ordini online integrati." },
  ],
  pt: [
    { title: "Interface intuitiva", desc: "Um sistema de caixa pensado para a rapidez e simplicidade." },
    { title: "Pagamento integrado", desc: "Terminais AyaPay ligados a caixa. Um sistema para tudo." },
    { title: "Analytics em tempo real", desc: "Acompanhe vendas, stocks e performance de qualquer dispositivo." },
    { title: "Gestao de stocks", desc: "Seguimento automatico, alertas e relatorios detalhados." },
    { title: "Multi-loja", desc: "Gira varios pontos de venda num painel centralizado." },
    { title: "Encomendas online", desc: "QR codes, tablets e encomendas online integradas." },
  ],
  tr: [
    { title: "Sezgisel arayuz", desc: "Hiz ve gunluk kullanim kolayligi icin tasarlanmis kasa sistemi." },
    { title: "Entegre odeme", desc: "AyaPay terminalleri kasaniza dogrudan bagli. Tek sistem." },
    { title: "Gercek zamanli analiz", desc: "Satislari, stoklari ve performansi her cihazdan takip edin." },
    { title: "Stok yonetimi", desc: "Otomatik stok takibi, yeniden siparis uyarilari." },
    { title: "Coklu sube", desc: "Birden fazla subeyi tek panelden yonetin." },
    { title: "Online siparisler", desc: "QR kodlar, tabletler ve online siparisler entegre." },
  ],
};

const INDUSTRIES_I18N = {
  fr: [
    { name: "Restaurants & Cafes", desc: "Gestion des tables, commandes cuisine, paiements fractionnes" },
    { name: "Commerce de detail", desc: "Codes-barres, programmes de fidelite, gestion des stocks" },
    { name: "Hotels", desc: "Check-in/out, facturation chambres, minibar" },
    { name: "Food trucks", desc: "Terminal mobile, gestion des files, paiement rapide" },
  ],
  de: [
    { name: "Restaurants & Cafes", desc: "Tischverwaltung, Kuchenbestellungen, geteilte Zahlungen" },
    { name: "Einzelhandel", desc: "Barcodes, Treueprogramme, Bestandsverwaltung" },
    { name: "Hotels", desc: "Check-in/out, Zimmerfakturierung, Minibar" },
    { name: "Food Trucks", desc: "Mobiles Terminal, Warteschlangen, schnelle Zahlung" },
  ],
  en: [
    { name: "Restaurants & Cafes", desc: "Table management, kitchen orders, split payments" },
    { name: "Retail", desc: "Barcodes, loyalty programs, inventory management" },
    { name: "Hotels", desc: "Check-in/out, room billing, minibar" },
    { name: "Food trucks", desc: "Mobile terminal, queue management, fast payment" },
  ],
  it: [
    { name: "Ristoranti & Caffe", desc: "Gestione tavoli, ordini cucina, pagamenti frazionati" },
    { name: "Commercio al dettaglio", desc: "Codici a barre, programmi fedelta, gestione stock" },
    { name: "Hotel", desc: "Check-in/out, fatturazione camere, minibar" },
    { name: "Food truck", desc: "Terminale mobile, gestione code, pagamento rapido" },
  ],
  pt: [
    { name: "Restaurantes & Cafes", desc: "Gestao de mesas, pedidos cozinha, pagamentos divididos" },
    { name: "Comercio a retalho", desc: "Codigos de barras, programas fidelidade, gestao stock" },
    { name: "Hoteis", desc: "Check-in/out, faturacao quartos, minibar" },
    { name: "Food trucks", desc: "Terminal movel, gestao de filas, pagamento rapido" },
  ],
  tr: [
    { name: "Restoranlar & Kafeler", desc: "Masa yonetimi, mutfak siparisleri, bolunmus odemeler" },
    { name: "Perakende", desc: "Barkodlar, sadakat programlari, stok yonetimi" },
    { name: "Oteller", desc: "Check-in/out, oda faturalama, minibar" },
    { name: "Food truck", desc: "Mobil terminal, kuyruk yonetimi, hizli odeme" },
  ],
};

export default function PosPage() {
  const { t, lang } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const features = POS_FEATURES_I18N[lang] || POS_FEATURES_I18N.fr;
  const industries = INDUSTRIES_I18N[lang] || INDUSTRIES_I18N.fr;
  const icons = [Monitor, CreditCard, BarChart3, ShoppingCart, Layers, Globe];

  return (
    <div data-testid="pos-page">
      <SEO page="pos" />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Free software banner */}
          <AnimatedSection className="mb-12">
            <div className="bg-[#0A1128] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 border border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00C853]/10 flex items-center justify-center shrink-0">
                  <Monitor size={24} className="text-[#00C853]" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Logiciel AyaPOS <span className="text-[#00C853]">100% gratuit</span></h3>
                  <p className="text-sm text-slate-400 mt-0.5">Pour tous les clients qui utilisent notre systeme de paiement AyaPay</p>
                </div>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-6 py-2.5 text-sm hover:bg-[#00A844] transition-all shrink-0">
                En profiter <ArrowRight size={16} />
              </Link>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pos.tag")}</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">{t("pos.title")}</h1>
              <p className="text-base md:text-lg text-[#475569] mt-6 leading-relaxed">{t("pos.desc")}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/contact" data-testid="pos-cta-demo" className="inline-flex items-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all">
                  {t("pos.ctaDemo")} <ArrowRight size={16} />
                </Link>
                <a href="https://ayapos.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center font-semibold border border-[#1B2B5B] text-[#1B2B5B] rounded-full px-7 py-3 text-sm hover:bg-[#1B2B5B] hover:text-white transition-all">
                  {t("pos.visitSite")}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                <img src="https://ayapos.com/uploads/e6a2a745-2f73-4f83-b7d3-2e676c753cff.jpg" alt="AyaPOS" loading="lazy" className="w-full h-80 lg:h-[420px] object-cover" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection>
              <div className="bg-white rounded-2xl border border-slate-200 p-8 flex items-center justify-center">
                <img src="https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL0pkUVoySEZ1VjhkVXRrR1pXNkJOLnBuZyJ9:adyen:9JEWptYxavUFw4vCOvtLSHBhds-Sy4FBjOEZDmV2Xvw?format=webp&width=500&height=666" alt="S1F2 - POS integre" loading="lazy" className="h-72 lg:h-96 object-contain" />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pos.futureTag")}</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
                {t("pos.futureTitle")}
              </h2>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">
                {t("pos.futureDesc1")}
              </p>
              <p className="text-base text-[#475569] mt-3 leading-relaxed">
                {t("pos.futureDesc2")}
              </p>
              <ul className="mt-6 space-y-3">
                {[t("pos.futureF1"), t("pos.futureF2"), t("pos.futureF3"), t("pos.futureF4"), t("pos.futureF5")].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="text-[#00C853] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/terminaux/s1f2" className="inline-flex items-center gap-2 mt-8 font-semibold bg-[#00C853] text-white rounded-full px-6 py-2.5 text-sm hover:bg-[#00A844] transition-all">
                {t("pos.futureCta")} <ArrowRight size={16} />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Screenshots showcase - BEFORE ecosystem */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">En images</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              Decouvrez l'interface AyaPOS
            </h2>
          </AnimatedSection>

          {/* Caisse */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img src="https://customer-assets.emergentagent.com/job_payment-links-hub-1/artifacts/qdpkjiq8_04_pos_with_items.png" alt="AyaPOS Caisse" loading="lazy" className="w-full h-auto" />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">Caisse</span>
              <h3 className="text-2xl font-semibold text-[#0A1128] tracking-tight mt-3">Prise de commande intuitive</h3>
              <p className="text-sm text-[#475569] mt-4 leading-relaxed">
                Interface de caisse avec categories colorees (Entrees, Plats, Pizzas, Burgers, Salades, Desserts, Boissons), commande en cours a gauche avec sous-total et TVA, et barre de navigation en bas (Caisse, Tables, Commandes, Reservations, Rapports).
              </p>
              <ul className="mt-5 space-y-2.5">
                {["Catalogue produits par categories", "Calcul automatique TVA suisse", "Sur place ou a emporter", "Envoi en cuisine en un clic", "Impression de notes et tickets"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <CheckCircle2 size={14} className="text-[#00C853] shrink-0" />
                    <span className="text-sm text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          </div>

          {/* Produits */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            <AnimatedSection delay={0.15} className="lg:order-2">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img src="https://customer-assets.emergentagent.com/job_payment-links-hub-1/artifacts/ntwqo3b8_07_products.png" alt="AyaPOS Produits" loading="lazy" className="w-full h-auto" />
              </div>
            </AnimatedSection>
            <AnimatedSection className="lg:order-1">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">Gestion</span>
              <h3 className="text-2xl font-semibold text-[#0A1128] tracking-tight mt-3">Produits, categories et supplements</h3>
              <p className="text-sm text-[#475569] mt-4 leading-relaxed">
                Gerez votre carte complete depuis l'interface : produits avec prix en CHF, categories, supplements. Ajoutez, modifiez ou supprimez en quelques secondes directement depuis la tablette ou le terminal.
              </p>
              <ul className="mt-5 space-y-2.5">
                {["Produits avec nom, categorie et prix", "Categories et sous-categories", "Supplements et options personnalisables", "Ajout rapide avec le bouton +", "Modification en temps reel sur tous les appareils"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <CheckCircle2 size={14} className="text-[#00C853] shrink-0" />
                    <span className="text-sm text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          </div>

          {/* Parametres */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img src="https://customer-assets.emergentagent.com/job_payment-links-hub-1/artifacts/tdjz8y9m_09_settings.png" alt="AyaPOS Parametres" loading="lazy" className="w-full h-auto" />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">Parametres</span>
              <h3 className="text-2xl font-semibold text-[#0A1128] tracking-tight mt-3">Configuration complete de votre commerce</h3>
              <p className="text-sm text-[#475569] mt-4 leading-relaxed">
                Parametrez tout votre ecosysteme depuis un seul ecran : informations entreprise, langue, apparence, caisse et paiements, impression et tickets, produits et inventaire, cuisine et production, vente en ligne, marketing et comptabilite.
              </p>
              <ul className="mt-5 space-y-2.5">
                {["Mon entreprise : nom, logo, adresse", "Apparence et theme du POS", "Caisse & Paiements configurable", "Impression & Tickets personnalises", "Cuisine & Production", "Vente en ligne et marketing integres", "Comptabilite & Assurances"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <CheckCircle2 size={14} className="text-[#00C853] shrink-0" />
                    <span className="text-sm text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Ecosysteme connecte */}
      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">Ecosysteme</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              Tout est connecte. Un seul ecosysteme.
            </h2>
            <p className="text-base text-[#475569] mt-4 max-w-2xl mx-auto">
              Prenez une commande sur la tablette, le telephone ou directement sur le terminal de paiement — tout est synchronise en temps reel. Un ecosysteme complet ou chaque appareil communique avec les autres.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Monitor, title: "Tablette / Caisse", desc: "Passez les commandes, gerez les tables, consultez les stocks. La caisse AyaPOS sur tablette est le cerveau de votre commerce.", color: "bg-[#00C853]" },
              { icon: Smartphone, title: "Telephone / Mobile", desc: "Prenez des commandes en salle, consultez les ventes en temps reel, recevez les notifications depuis votre poche.", color: "bg-[#1B2B5B]" },
              { icon: CreditCard, title: "Terminal de paiement", desc: "Encaissez directement sur le terminal S1F2 avec la caisse integree. Paiement + commande dans un seul appareil.", color: "bg-[#00C853]" },
            ].map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-100 p-8 h-full text-center">
                  <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-5`}>
                    <item.icon size={26} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div className="bg-[#0A1128] rounded-2xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white">Synchronisation en temps reel</h3>
                  <p className="text-slate-300 text-sm mt-3 leading-relaxed">
                    Une commande passee sur la tablette apparait instantanement sur le terminal. Un paiement effectue sur le S1F2 met a jour la caisse automatiquement. Tout est connecte, rien n'est perdu.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    "Commandes synchronisees",
                    "Stock mis a jour en temps reel",
                    "Paiements reconcilies automatiquement",
                    "Rapports consolides multi-appareils",
                    "Un seul back-office pour tout",
                    "Fonctionne en WiFi et 4G/5G"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#00C853] shrink-0" />
                      <span className="text-sm text-slate-200">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-[#00C853]/10 flex items-center justify-center">
                    <Zap size={24} className="text-[#00C853]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0A1128]">AyaPay + AyaPOS</div>
                    <div className="text-xs text-[#475569]">{t("pos.integrationTag")}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  {[t("pos.intF1"), t("pos.intF2"), t("pos.intF3"), t("pos.intF4")].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg">
                      <CheckCircle2 size={16} className="text-[#00C853] shrink-0" />
                      <span className="text-sm text-[#475569]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pos.integrationTag")}</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("pos.integrationTitle")}</h2>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">{t("pos.integrationDesc")}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-6 font-semibold bg-[#00C853] text-white rounded-full px-6 py-2.5 text-sm hover:bg-[#00A844] transition-all">
                {t("solutions.learnMore")} <ArrowRight size={16} />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pos.featuresTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("pos.featuresTitle")}</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, i) => {
              const Icon = icons[i];
              return (
                <AnimatedSection key={i} delay={i * 0.08}>
                  <div className="feature-card bg-white rounded-xl border border-slate-200 p-8 h-full">
                    <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-5">
                      <Icon size={22} className="text-[#00C853]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{feat.title}</h3>
                    <p className="text-sm text-[#475569] leading-relaxed">{feat.desc}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pos.sectorsTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("pos.sectorsTitle")}</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {industries.map((ind, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-100 p-8">
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{ind.name}</h3>
                  <p className="text-sm text-[#475569]">{ind.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{t("pos.ctaBottom")}</h2>
            <p className="text-base text-slate-300 mt-4 max-w-md mx-auto">{t("pos.ctaBottomDesc")}</p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/contact" data-testid="pos-cta-bottom" className="inline-flex items-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all">
                {t("pos.ctaDemo")} <ArrowRight size={16} />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
